import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getMyDetails, getTutorDetails } from "@/api/user.ts";
import { IsAdmin } from "@/types/user.ts";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { TutorStatus } from "@/types/tutor.ts";
import { SigRInvoke } from "@/types/signalr-functions.ts";
import { toastAndError } from "@/helpers/toast.ts";
import { useUserStore } from "@/stores/useUserStore.ts";
import { Some } from "ts-results-es";
import { useSignalStore } from "@/stores/useSignalrStore.ts";

export const useHookTutorLogin = () => {
  const {
    auth,
    userId,
    tutorId,
    userDetails,
    SetUserDetails,
    SetTutorDetails,
    settings,
    SetGroupJoined,
    Logout,
    SetStatusEvent,
    SetCapacity,
  } = useUserStore();

  const joiningRef = useRef(false);
  const queryClient = useQueryClient();
  const { signalr } = useSignalStore();

  const fetchUserDetails = useQuery({
    queryFn: getMyDetails,
    queryKey: ["me", auth.token],
    enabled: auth.token !== null && userDetails === null,
    staleTime: 1000 * 60 * 60 * 24,
    retry: 5,
  });

  const fetchTutorDetails = useQuery({
    queryFn: () => getTutorDetails(false),
    queryKey: ["tutor", auth.token],
    enabled: userId !== undefined && userId !== -1 && IsAdmin(userDetails?.userRoles ?? []),
    staleTime: 1000 * 60,
    retry: 5,
  });

  /// Store our user & tutor details if successful
  const loadingRef = useRef<string | number | null>(null);
  useEffect(() => {
    if (
      loadingRef.current === null &&
      (fetchUserDetails.isLoading || fetchTutorDetails.isLoading)
    ) {
      loadingRef.current = toast.message("Loading user/tutor details...", {
        duration: Infinity,
        closeButton: true,
      });
    } else if (
      loadingRef.current !== null &&
      fetchTutorDetails.isFetched &&
      fetchUserDetails.isFetched
    ) {
      toast.dismiss(loadingRef.current);
      loadingRef.current = null;
    }

    if (fetchUserDetails.isSuccess) {
      SetUserDetails(fetchUserDetails.data);

      if (!fetchUserDetails.data.isAdmin) {
        Logout(true);
      }
    }

    return () => {
      if (loadingRef.current !== null) {
        toast.dismiss(loadingRef.current);
      }
    };
  }, [
    Logout,
    SetUserDetails,
    fetchTutorDetails.isFetched,
    fetchTutorDetails.isLoading,
    fetchUserDetails.data,
    fetchUserDetails.error,
    fetchUserDetails.isError,
    fetchUserDetails.isFetched,
    fetchUserDetails.isLoading,
    fetchUserDetails.isSuccess,
  ]);

  useEffect(() => {
    if (fetchTutorDetails.isSuccess && tutorId === -1) {
      SetTutorDetails(fetchTutorDetails.data);
    }
  }, [
    auth.token,
    fetchTutorDetails.data,
    fetchTutorDetails.isSuccess,
    fetchTutorDetails.isError,
    SetTutorDetails,
    fetchUserDetails.error,
    tutorId,
  ]);
  ///

  /// Fetch our status (fixes some edge cases) from an endpoint
  useEffect(() => {
    let removeTimeout: number | undefined;

    const fetchStatus = () => {
      if (!auth.token || userId === -1) return;

      queryClient
        .query({
          queryFn: () => getTutorDetails(true),
          queryKey: ["tutor", auth.token],
          staleTime: 500,
          retry: 5,
        })
        .then((data) => {
          const status = data.status === "Available" ? TutorStatus.Available : TutorStatus.Offline;

          SetStatusEvent(Some(status));
          if (data.capacity) SetCapacity(data.capacity);
        });

      removeTimeout = setTimeout(
        () => {
          fetchStatus();
        },
        1000 * 60 * 5,
      );
    };

    fetchStatus();

    return () => {
      if (removeTimeout) clearTimeout(removeTimeout);
    };
  }, [SetCapacity, SetStatusEvent, auth.token, userId, queryClient]);
  ///

  /// If we update our settings (capacity, status), join/leave tutor group
  useEffect(() => {
    if (!signalr || joiningRef.current) return;

    if (
      !settings.groupJoined &&
      settings.capacity > 0 &&
      settings.status === TutorStatus.Available
    ) {
      joiningRef.current = true;
      signalr
        ?.Invoke(SigRInvoke.JoinTutorGroup, tutorId.toString())
        .then(() => {
          SetGroupJoined(true);
        })
        .catch((error) => {
          toastAndError("Unable to join tutor group, most likely lost internet connection", error);
          SetGroupJoined(false);
        })
        .finally(() => {
          joiningRef.current = false;
        });
    }

    if (
      settings.groupJoined &&
      (settings.status === TutorStatus.Offline || settings.capacity === 0)
    ) {
      joiningRef.current = true;
      signalr
        .Invoke(SigRInvoke.LeaveTutorGroup, tutorId.toString())
        .then(() => {
          SetGroupJoined(false);
        })
        .catch((error) => {
          toastAndError("Unable to leave tutor group", error);
          SetGroupJoined(true);
        })
        .finally(() => {
          joiningRef.current = false;
        });
    }
  }, [SetGroupJoined, settings, tutorId, queryClient, signalr]);
  ///
};
